import { Button } from "@/components/ui/button";
import { getMainLanguage } from "@/i18n";
import {
  captionAlignmentToTextAlignment,
  LayoutNonNullable,
  LayoutNumberOfColumns,
  Section,
  SectionDescriptionVisibility,
  Selection,
} from "@/lib/cms/section-types";
import { ImageSize } from "@/lib/types";
import {
  chunkArray,
  chunkArrayVertically,
  cn,
  getImageSize,
  getPaddingToApplyToCenter,
} from "@/lib/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { SelectionCaption } from "./SelectionCaption";
import { SelectionItemMedia } from "./SelectionItemMedia";

type DisplayableTypeMap = {
  section: Section;
  selection: Selection;
};

type SupportedDataTypes = DisplayableTypeMap[keyof DisplayableTypeMap];

type GridDisplayProps = {
  [K in keyof DisplayableTypeMap]: {
    displayable: {
      dType: K;
      data: DisplayableTypeMap[K][];
    };
    layout: LayoutNonNullable;
  } & (K extends "selection"
    ? { visibility: SectionDescriptionVisibility }
    : { visibility?: never });
}[keyof DisplayableTypeMap];

export const GridDisplay = ({
  displayable,
  layout,
  visibility,
}: GridDisplayProps) => {
  const { data, dType } = displayable;

  const dataChunks = useMemo(
    () =>
      layout.alignment !== "SPACE_AROUND"
        ? chunkArray<SupportedDataTypes>(data, layout.numberOfColumns)
        : chunkArrayVertically<SupportedDataTypes>(
            data,
            layout.numberOfColumns,
          ),
    [data, layout],
  );

  return (
    <div
      className={cn(
        `w-full flex gap-8 ${layout.alignment === "SPACE_AROUND" ? "flex-wrap" : "flex-col"} `,
      )}
    >
      {(() => {
        switch (dType) {
          case "section":
            return dataChunks.map((chunk, index) => (
              <RowDisplaySection
                key={index}
                layout={layout}
                rowData={chunk as Section[]}
              />
            ));
          case "selection":
            return dataChunks.map((chunk, index) => (
              <RowDisplaySelection
                key={index}
                layout={layout}
                visibility={visibility!}
                rowData={chunk as Selection[]}
              />
            ));
        }
      })()}
    </div>
  );
};

type RowDisplaySelectionProps = {
  layout: LayoutNonNullable;
  visibility: SectionDescriptionVisibility;
  rowData: Selection[];
};

const RowDisplaySelection = ({
  rowData,
  layout,
  visibility,
}: RowDisplaySelectionProps) => {
  return (
    <RowDisplay
      data={rowData}
      dType="selection"
      layout={layout}
      render={({ item, layout, paddingTop }) => {
        return (
          <div
            key={item.item.id}
            className="flex-col justify-start items-center inline-flex relative"
            style={{
              width: `${layout.alignment === "SPACE_AROUND" ? 100 : getMaxWidthPercent(isMobile, layout.numberOfColumns)}%`,
            }}
          >
            <div
              style={{
                paddingTop,
              }}
              className="w-full"
            >
              <SelectionItemMedia item={item.item} />
            </div>
            <SelectionCaption
              item={item.item}
              caption={item.caption}
              visibility={visibility}
              captionAlignment={layout.captionAlignment}
            />
          </div>
        );
      }}
    />
  );
};

type RowDisplaySectionProps = {
  layout: LayoutNonNullable;
  rowData: Section[];
};

const RowDisplaySection = ({ rowData, layout }: RowDisplaySectionProps) => {
  const { t } = useTranslation();

  return (
    <RowDisplay
      data={rowData}
      dType="section"
      layout={layout}
      render={({ item, layout, paddingTop }) => (
        <div
          key={item.id}
          className="flex-col justify-start items-center inline-flex relative"
          style={{
            width: `${layout.alignment === "SPACE_AROUND" ? 100 : getMaxWidthPercent(isMobile, layout.numberOfColumns)}%`,
          }}
        >
          <div
            style={{
              paddingTop,
            }}
            className={`w-full`}
          >
            <img
              className="relative"
              src={item.thumbnail?.mediumPublicUrl ?? ""}
              alt={item.content?.[getMainLanguage()]?.title ?? ""}
            />
          </div>
          <div
            className={`flex-col w-full gap-1`}
            style={{
              textAlign: captionAlignmentToTextAlignment(
                layout.captionAlignment,
              ),
            }}
          >
            <div className="self-stretch py-2 flex-col justify-start items-start gap-1 flex">
              <span
                className="text-[#090909] text-base font-semibold font-['Instrument Sans'] leading-[14.40px] w-full"
                style={{
                  textAlign: captionAlignmentToTextAlignment(
                    layout.captionAlignment,
                  ),
                }}
              >
                {item.content?.[getMainLanguage()]?.title}
              </span>
              <div
                className="text-[#090909] text-xs w-full font-normal font-['Instrument Sans'] leading-[14.40px]"
                style={{
                  textAlign: captionAlignmentToTextAlignment(
                    layout.captionAlignment,
                  ),
                }}
                dangerouslySetInnerHTML={{
                  __html: item.content?.[getMainLanguage()]?.content ?? "",
                }}
              />
            </div>
            <Link to={`/cms/selection/section/${item.id}`}>
              <Button
                variant="outline"
                className="flex max-w-fit overflow-hidden"
              >
                {t("cms.selection.manageSection.button")}
              </Button>
            </Link>
          </div>
        </div>
      )}
    />
  );
};

type RowDisplayProps<K extends keyof DisplayableTypeMap> = {
  data: DisplayableTypeMap[K][];
  dType: K;
  layout: LayoutNonNullable;
  render: (props: {
    item: DisplayableTypeMap[K];
    layout: LayoutNonNullable;
    paddingTop: string;
  }) => JSX.Element;
};

const RowDisplay = <K extends keyof DisplayableTypeMap>({
  data,
  dType,
  layout,
  render,
}: RowDisplayProps<K>) => {
  const { numberOfColumns, alignment } = layout;
  const ref = useRef<HTMLDivElement>(null);

  const [sizes, setSizes] = useState<ImageSize[]>(() =>
    new Array(data.length).fill({ width: 0, height: 0 }),
  );

  useEffect(() => {
    const getUrls = () => {
      switch (dType) {
        case "section":
          return (data as Section[]).map(
            (d) => d.thumbnail?.mediumPublicUrl ?? "",
          );
        case "selection":
          return (data as Selection[]).map((d) => d.item.url ?? "");
        default:
          throw new Error("Unmanaged dType");
      }
    };

    const rowUrls = getUrls();

    Promise.all(rowUrls.map(getImageSize)).then((sizes) => setSizes(sizes));
  }, [data, dType]);

  return (
    <div
      className={cn(
        `w-full flex gap-8 ${alignment === "SPACE_AROUND" ? "flex-col" : "flex-wrap"}`,
      )}
      style={{
        width: `${alignment === "SPACE_AROUND" ? getMaxWidthPercent(isMobile, numberOfColumns) : 100}%`,
      }}
      ref={ref}
    >
      {data.map((item, index) => {
        const paddingTop = `${alignment === "CENTER" ? getPaddingToApplyToCenter(index, sizes, ref.current?.offsetWidth ? (ref.current.offsetWidth * getMaxWidthPercent(isMobile, layout?.numberOfColumns)) / 100 : 0) : 0}px`;
        return render({ item, layout, paddingTop });
      })}
    </div>
  );
};

const getMaxWidthPercent = (
  isMobile: boolean,
  numberOfColumns: LayoutNumberOfColumns,
) => {
  return isMobile
    ? 100
    : numberOfColumns === 2
      ? 45
      : numberOfColumns === 3
        ? 30
        : numberOfColumns === 4
          ? 22
          : 30;
};
